import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Clock, Zap, BookOpen, Wrench, ListChecks, MessageSquare, Link2, XCircle, Loader2, Trophy, Upload, Send, FileCode, Layers, Camera, FileText, FolderGit2, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fireConfetti } from "@/lib/confetti";
import AIReportCard from "@/components/AIReportCard";
import DiscussionThread from "@/components/DiscussionThread";
import InterviewPrepCard from "@/components/InterviewPrepCard";

const TABS = [
  { value: "overview", label: "Overview", icon: BookOpen },
  { value: "lab", label: "Hands-on Lab", icon: Wrench },
  { value: "quiz", label: "Quiz", icon: ListChecks },
  { value: "discussion", label: "Discussion", icon: MessageSquare },
  { value: "resources", label: "Resources", icon: Link2 },
];

const CAPSTONE_CATEGORIES = [
  { key: "project", label: "Project", icon: FolderGit2, placeholder: "Describe your capstone project overview..." },
  { key: "sql", label: "Apex / Automation", icon: FileCode, placeholder: "Paste your Apex code / Flow logic..." },
  { key: "architecture", label: "Architecture", icon: Layers, placeholder: "Describe your Admin + Dev solution architecture (data model, security, automation, custom code)..." },
  { key: "screenshots", label: "Screenshots", icon: Camera, placeholder: "Describe screenshots or paste image links..." },
  { key: "documentation", label: "Documentation", icon: FileText, placeholder: "Write your resume bullet points + interview answers here..." },
];

export default function AdminDevChallengeDetailPage() {
  const { day } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [labText, setLabText] = useState("");
  const [submittingLab, setSubmittingLab] = useState(false);
  const [labResult, setLabResult] = useState(null);
  const [capstoneText, setCapstoneText] = useState({});
  const [capstoneResults, setCapstoneResults] = useState({});
  const [submittingCategory, setSubmittingCategory] = useState(null);
  const [discussions, setDiscussions] = useState([]);
  const [newMessage, setNewMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/admin-dev/challenges/${day}`);
      setChallenge(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Unable to load this challenge");
    } finally {
      setLoading(false);
    }
  }, [day]);

  const loadDiscussions = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin-dev/discussions/${day}`);
      setDiscussions(data);
    } catch (e) {
      console.error("Failed to load Admin + Dev discussions:", e);
    }
  }, [day]);

  useEffect(() => {
    load();
    loadDiscussions();
    setAnswers({});
    setQuizResult(null);
    setLabResult(null);
    setLabText("");
  }, [day, load, loadDiscussions]);

  const submitLab = async () => {
    if (!labText.trim()) {
      toast.error("Please describe your solution before submitting");
      return;
    }
    setSubmittingLab(true);
    try {
      const formData = new FormData();
      formData.append("sub_type", "text");
      formData.append("text_content", labText);
      const { data } = await api.post(`/admin-dev/submissions/${day}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLabResult(data);
      toast.success("Submission reviewed by AI Project Evaluator");
      refreshUser();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmittingLab(false);
    }
  };

  const submitCapstoneCategory = async (categoryKey) => {
    const text = capstoneText[categoryKey];
    if (!text?.trim()) {
      toast.error("Please add content before submitting this category");
      return;
    }
    setSubmittingCategory(categoryKey);
    try {
      const formData = new FormData();
      formData.append("sub_type", categoryKey);
      formData.append("text_content", text);
      const { data } = await api.post(`/admin-dev/submissions/${day}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCapstoneResults((prev) => ({ ...prev, [categoryKey]: data }));
      toast.success(`${categoryKey} reviewed by AI Project Evaluator`);
      refreshUser();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmittingCategory(null);
    }
  };

  const postMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      await api.post(`/admin-dev/discussions/${day}`, { message: newMessage });
      setNewMessage("");
      loadDiscussions();
    } catch (e) {
      toast.error("Failed to post message");
    }
  };

  const postReply = async (parentId, replyText) => {
    try {
      await api.post(`/admin-dev/discussions/${day}`, { message: replyText, parent_id: parentId });
      loadDiscussions();
    } catch (e) {
      toast.error("Failed to post reply");
    }
  };

  const toggleLike = async (id) => {
    try {
      await api.post(`/admin-dev/discussions/message/${id}/like`);
      loadDiscussions();
    } catch (e) {
      console.error("Failed to toggle like:", e);
    }
  };

  const submitQuiz = async () => {
    setSubmittingQuiz(true);
    try {
      const { data } = await api.post(`/admin-dev/challenges/${day}/quiz`, { answers });
      setQuizResult(data);
      if (data.passed) {
        toast.success(`Admin + Dev Day ${day} completed! +${data.xp_reward} XP earned`);
        fireConfetti();
        refreshUser();
      } else {
        toast.error(`You scored ${data.quiz_score}%. Need 80% to pass — try again!`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to submit quiz");
    } finally {
      setSubmittingQuiz(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-dev-challenge-loading">
        <div className="w-10 h-10 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !challenge) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" data-testid="admin-dev-challenge-error">
        <p className="text-slate-400">{error || "Challenge not found"}</p>
        <Link to="/admin-dev" className="text-amber-400 hover:underline text-sm">Back to Admin + Dev Roadmap</Link>
      </div>
    );
  }

  const mcqQuestions = challenge.quiz.filter((q) => q.type === "mcq");
  const scenarioQuestion = challenge.quiz.find((q) => q.type === "scenario");
  const allAnswered = mcqQuestions.every((q) => answers[q.id] !== undefined) && (answers[scenarioQuestion?.id] || "").trim();

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-dev-challenge-detail-page">
      <Link to="/admin-dev" data-testid="admin-dev-challenge-back-link" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Admin + Dev Roadmap
      </Link>

      <div className="flex items-center gap-3 mb-1">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400">Admin + Dev Day {challenge.day}</span>
      </div>
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white">{challenge.title}</h1>
      <div className="flex items-center gap-4 mt-3">
        <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {challenge.estimated_minutes}m</span>
        <span className="text-xs font-bold text-[#FBBF24] flex items-center gap-1"><Zap className="w-3.5 h-3.5" /> +{challenge.xp_reward} XP</span>
      </div>

      <Tabs defaultValue="overview" className="mt-6">
        <TabsList className="bg-white/5 p-1 flex-wrap h-auto">
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value} data-testid={`admin-dev-tab-${t.value}`} className="data-[state=active]:bg-amber-500 data-[state=active]:text-white flex items-center gap-1.5">
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-4" data-testid="admin-dev-tab-content-overview">
          <div className="glass-panel rounded-2xl p-6">
            <h2 className="font-heading text-sm font-semibold text-white mb-2">Overview</h2>
            <p className="text-sm text-slate-300 leading-relaxed">{challenge.description}</p>
          </div>
          <div className="glass-panel rounded-2xl p-6">
            <h2 className="font-heading text-sm font-semibold text-white mb-2">Business Scenario</h2>
            <p className="text-sm text-slate-300 leading-relaxed italic">{challenge.business_scenario}</p>
          </div>
          <div className="glass-panel rounded-2xl p-6">
            <h2 className="font-heading text-sm font-semibold text-white mb-2">Reading Notes</h2>
            <ul className="space-y-2">
              {challenge.reading_notes.map((n) => (
                <li key={n} className="text-sm text-slate-300 flex gap-2"><span className="text-amber-400">•</span> {n}</li>
              ))}
            </ul>
          </div>
          <InterviewPrepCard track="admin_dev" day={parseInt(day)} accent="amber" />
        </TabsContent>

        <TabsContent value="lab" className="space-y-4 mt-4" data-testid="admin-dev-tab-content-lab">
          <div className="glass-panel rounded-2xl p-6">
            <h3 className="font-heading font-semibold text-white mb-2 flex items-center gap-2"><Wrench className="w-4 h-4 text-amber-400" /> Hands-on Lab</h3>
            <p className="text-sm text-slate-300 leading-relaxed">{challenge.hands_on_lab}</p>
          </div>

          {challenge.day === 30 ? (
            <div className="space-y-4" data-testid="admin-dev-capstone-submission-section">
              <div className="glass-panel rounded-2xl p-4 border border-purple-400/20 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-purple-400" />
                <p className="text-xs text-purple-300 font-semibold">Final Admin + Dev Capstone Project — submit each category below for AI evaluation</p>
              </div>
              {CAPSTONE_CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                const result = capstoneResults[cat.key];
                return (
                  <div key={cat.key} className="glass-panel rounded-2xl p-6" data-testid={`admin-dev-capstone-category-${cat.key}`}>
                    <h4 className="font-heading font-semibold text-white text-sm mb-3 flex items-center gap-2">
                      <Icon className="w-4 h-4 text-amber-400" /> {cat.label}
                      {result && <CheckCircle2 className="w-4 h-4 text-emerald-400 ml-auto" />}
                    </h4>
                    <textarea
                      value={capstoneText[cat.key] || ""} rows={3}
                      onChange={(e) => setCapstoneText((prev) => ({ ...prev, [cat.key]: e.target.value }))}
                      placeholder={cat.placeholder} data-testid={`admin-dev-capstone-textarea-${cat.key}`}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
                    />
                    <button
                      onClick={() => submitCapstoneCategory(cat.key)} disabled={submittingCategory === cat.key}
                      data-testid={`admin-dev-capstone-submit-${cat.key}`}
                      className="mt-3 flex items-center gap-2 bg-white/10 hover:bg-white/15 disabled:opacity-60 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
                    >
                      {submittingCategory === cat.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      {result ? "Resubmit" : "Submit"} {cat.label}
                    </button>
                    <AIReportCard result={result} />
                  </div>
                );
              })}
              {Object.keys(capstoneResults).length === CAPSTONE_CATEGORIES.length && (
                <div className="glass-panel rounded-2xl p-6 text-center border border-emerald-400/30" data-testid="admin-dev-capstone-marked-submitted">
                  <Trophy className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-emerald-400">Admin + Dev Capstone Project Marked as Submitted!</p>
                  <p className="text-xs text-slate-400 mt-1">Now complete the Quiz tab to finish Day 30.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="glass-panel rounded-2xl p-6">
              <h3 className="font-heading font-semibold text-white mb-3">Submit Your Solution</h3>
              <textarea
                value={labText} onChange={(e) => setLabText(e.target.value)} rows={5}
                data-testid="admin-dev-lab-submission-textarea"
                placeholder="Describe your solution, paste Apex/config, or summarize your design..."
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
              />
              <button
                onClick={submitLab} disabled={submittingLab} data-testid="admin-dev-lab-submit-button"
                className="mt-3 flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
              >
                {submittingLab ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Submit for AI Review
              </button>
              <AIReportCard result={labResult} />
            </div>
          )}
        </TabsContent>

        <TabsContent value="quiz" className="space-y-4 mt-4" data-testid="admin-dev-tab-content-quiz">
          {mcqQuestions.map((q, i) => (
            <div key={q.id} className="glass-panel rounded-2xl p-6" data-testid={`admin-dev-quiz-mcq-${i}`}>
              <p className="text-sm font-medium text-white mb-3">{i + 1}. {q.question}</p>
              <div className="space-y-2">
                {q.options.map((opt, idx) => {
                  const selected = answers[q.id] === String(idx);
                  const feedback = quizResult?.mcq_feedback?.find((f) => f.id === q.id);
                  let stateClass = selected ? "border-amber-400 bg-amber-400/10" : "border-white/10 hover:border-white/25";
                  if (feedback) {
                    if (idx === feedback.correct_index) stateClass = "border-emerald-400 bg-emerald-400/10";
                    else if (selected && !feedback.correct) stateClass = "border-red-400 bg-red-400/10";
                  }
                  return (
                    <button
                      key={idx} disabled={!!quizResult}
                      onClick={() => setAnswers((prev) => ({ ...prev, [q.id]: String(idx) }))}
                      data-testid={`admin-dev-quiz-mcq-${i}-option-${idx}`}
                      className={`w-full text-left text-sm px-4 py-2.5 rounded-lg border transition-colors text-slate-200 ${stateClass}`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {scenarioQuestion && (
            <div className="glass-panel rounded-2xl p-6" data-testid="admin-dev-quiz-scenario">
              <p className="text-sm font-medium text-white mb-3">{mcqQuestions.length + 1}. {scenarioQuestion.question}</p>
              <textarea
                value={answers[scenarioQuestion.id] || ""} disabled={!!quizResult}
                onChange={(e) => setAnswers((prev) => ({ ...prev, [scenarioQuestion.id]: e.target.value }))}
                rows={4} data-testid="admin-dev-quiz-scenario-textarea"
                placeholder="Write your answer here..."
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-amber-400 disabled:opacity-70"
              />
              {quizResult?.scenario_feedback && (
                <div className="mt-3 border border-amber-400/20 rounded-lg p-3 bg-amber-400/5">
                  <p className="text-xs text-slate-300">{quizResult.scenario_feedback}</p>
                </div>
              )}
            </div>
          )}

          {!quizResult ? (
            <button
              onClick={submitQuiz} disabled={!allAnswered || submittingQuiz} data-testid="admin-dev-quiz-submit-button"
              className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-white font-semibold py-3 rounded-lg transition-colors"
            >
              {submittingQuiz ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListChecks className="w-4 h-4" />} Submit Quiz
            </button>
          ) : (
            <div
              className={`rounded-2xl p-6 text-center border ${quizResult.passed ? "border-emerald-400/40 bg-emerald-400/5" : "border-red-400/40 bg-red-400/5"}`}
              data-testid="admin-dev-quiz-result-panel"
            >
              {quizResult.passed ? (
                <Trophy className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
              ) : (
                <XCircle className="w-10 h-10 text-red-400 mx-auto mb-2" />
              )}
              <h3 className="font-heading text-xl font-semibold text-white">
                {quizResult.passed ? `Admin + Dev Day ${challenge.day} Complete!` : "Not quite — try again"}
              </h3>
              <p className="text-sm text-slate-400 mt-1">Score: {quizResult.quiz_score}% {quizResult.passed && `· +${quizResult.xp_reward} XP earned`}</p>
              <div className="flex items-center justify-center gap-3 mt-5">
                {!quizResult.passed && (
                  <button onClick={() => { setQuizResult(null); setAnswers({}); }} data-testid="admin-dev-quiz-retry-button" className="text-sm font-semibold px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-white transition-colors">
                    Retry Quiz
                  </button>
                )}
                {quizResult.passed && challenge.day < 30 && (
                  <button onClick={() => navigate(`/admin-dev/challenge/${parseInt(day) + 1}`)} data-testid="admin-dev-quiz-next-day-button" className="text-sm font-semibold px-5 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-white transition-colors">
                    Next Challenge →
                  </button>
                )}
                {quizResult.passed && challenge.day === 30 && (
                  <button onClick={() => navigate("/admin-dev/certificate")} data-testid="admin-dev-quiz-certificate-button" className="text-sm font-semibold px-5 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-white transition-colors">
                    Claim Your Certificate →
                  </button>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="discussion" className="space-y-4 mt-4" data-testid="admin-dev-tab-content-discussion">
          <div className="glass-panel rounded-2xl p-6">
            <div className="flex gap-2">
              <input
                value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && postMessage()}
                placeholder="Ask a question or share your insight..." data-testid="admin-dev-discussion-input"
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
              />
              <button onClick={postMessage} data-testid="admin-dev-discussion-post-button" className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 rounded-lg">
                <Send className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
          <DiscussionThread discussions={discussions} onLike={toggleLike} onReply={postReply} />
        </TabsContent>

        <TabsContent value="resources" className="space-y-3 mt-4" data-testid="admin-dev-tab-content-resources">
          {challenge.resources.map((r) => (
            <a key={r.url} href={r.url} target="_blank" rel="noopener noreferrer" className="glass-panel rounded-xl p-4 flex items-center gap-3 hover:border-amber-400/40 transition-colors block">
              <Link2 className="w-4 h-4 text-amber-400" />
              <span className="text-sm text-slate-200">{r.title}</span>
            </a>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
